import { ArrowRight, Compass, RefreshCw, Trash2 } from "lucide-react";
import { CoachIcon } from "@/components/CoachIcon";
import { SchoolBrandHeader } from "@/components/school/SchoolBrandHeader";
import { SchoolFitSummary } from "@/components/school/SchoolFitSummary";
import type { PipelineEntry } from "@/services/pipeline";
import { PIPELINE_STAGES, nextRelationshipAction, stageIndex } from "./pipelineDesign";
import { FitComparison } from "@/components/school/FitComparison";

interface SchoolPortfolioCardProps {
  entry: PipelineEntry;
  onOpen: (entry: PipelineEntry) => void;
  onRemove: (entry: PipelineEntry) => void;
  onRefreshFit: (entry: PipelineEntry) => void;
}

function FitState({ entry, onRefresh }: { entry: PipelineEntry; onRefresh: () => void }) {
  const fit = entry.golfFit;
  const status = fit?.status || "missing";
  const isWorking = status === "missing" || status === "updating" || status === "stale";

  if (status === "failed") {
    return (
      <div className="rounded-[14px] border border-warn-border bg-warn-soft p-3.5">
        <div className="flex items-center gap-2 text-xs font-bold text-warn">
          <CoachIcon className="h-4 w-4" /> AI Coach fit
        </div>
        <p className="mt-2 text-[11px] leading-5 text-warn">
          The assessment could not be refreshed. Your school and notes are still here.
        </p>
        <button
          type="button"
          onClick={event => {
            event.stopPropagation();
            onRefresh();
          }}
          className="mt-2 inline-flex items-center gap-1.5 text-[11px] font-extrabold text-warn hover:underline"
        >
          <RefreshCw className="h-3 w-3" /> Try again
        </button>
      </div>
    );
  }

  return (
    <div className="flex items-start gap-2">
      <span
        aria-hidden="true"
        className="mt-0.5 grid h-5 w-5 shrink-0 place-items-center rounded-md bg-lime-soft text-primary-text"
      >
        <CoachIcon className="h-4 w-4" />
      </span>
      <div className="min-w-0 flex-1">
        <span className="sr-only">AI Coach notes: </span>
        <SchoolFitSummary
          markdown={fit?.summary}
          fallback={
            status === "unavailable"
              ? "The AI Coach needs more verified golf data before it can score this program."
              : "The AI Coach is reviewing your current golf data against this program."
          }
          lines={2}
          className="text-xs leading-[1.5]"
        />
        {isWorking && (
          <span className="mt-1 inline-flex items-center gap-1.5 text-[11px] font-bold text-subtle">
            <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-primary" />
            {fit?.score != null ? "Refreshing" : "Preparing"}
          </span>
        )}
      </div>
    </div>
  );
}

export function SchoolPortfolioCard({
  entry,
  onOpen,
  onRemove,
  onRefreshFit,
}: SchoolPortfolioCardProps) {
  const fit = entry.golfFit;
  const currentStage = stageIndex(entry.stage);
  const evidence = fit?.evidence;

  return (
    <article
      role="button"
      tabIndex={0}
      aria-label={`Open ${entry.school.schoolName}`}
      onClick={() => onOpen(entry)}
      onKeyDown={event => {
        if (event.target !== event.currentTarget) return;
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onOpen(entry);
        }
      }}
      className="group relative min-w-0 overflow-hidden rounded-[18px] border border-border bg-white shadow-[0_8px_24px_rgba(26,43,35,0.05)] transition duration-200 hover:-translate-y-0.5 hover:border-border-strong hover:shadow-[0_18px_38px_rgba(26,43,35,0.11)] focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-primary/25"
    >
      <SchoolBrandHeader school={entry.school} fit={fit} variant="compact" />

      <div className="p-3.5">
        {entry.recommendationContext === "exploration" && (
          <div className="mb-3 flex items-start gap-2 rounded-xl border border-border bg-surface px-3 py-2.5 text-forest-soft">
            <Compass className="mt-0.5 h-3.5 w-3.5 shrink-0 text-primary-text" />
            <div className="min-w-0">
              <strong className="block text-xs font-bold text-forest">Program to explore</strong>
              <span className="mt-0.5 block text-[11px] leading-snug text-muted-foreground">
                {entry.recommendationReason}
              </span>
            </div>
          </div>
        )}
        {entry.recommendationContext === "perspective" && (
          <div className="mb-3 flex items-start gap-2 rounded-xl border border-lime-border bg-lime-soft px-3 py-2.5 text-forest-soft">
            <Compass className="mt-0.5 h-3.5 w-3.5 shrink-0 text-primary-text" />
            <div className="min-w-0">
              <strong className="block text-xs font-bold text-forest">Perspective pick</strong>
              <span className="mt-0.5 block text-[11px] leading-snug text-muted-foreground">
                {entry.recommendationReason || "A strong golf fit worth considering"}
              </span>
            </div>
          </div>
        )}
        <FitState entry={entry} onRefresh={() => onRefreshFit(entry)} />

        <FitComparison
          playerRank={evidence?.playerClassRank}
          programRank={evidence?.programAverageClassRank}
          playerDifferential={evidence?.playerScoringDifferential}
          programDifferential={evidence?.programAverageScoringDifferential}
          className="mt-2"
        />

        <ol className="mt-3.5 grid grid-cols-5" aria-label="Relationship stage">
          {PIPELINE_STAGES.map((stage, index) => {
            const last = index === PIPELINE_STAGES.length - 1;
            const isCurrent = index === currentStage;
            return (
              <li
                key={stage.id}
                aria-current={isCurrent ? "step" : undefined}
                className="relative flex flex-col items-center"
              >
                {index > 0 && (
                  <span
                    aria-hidden="true"
                    className={`absolute left-0 right-1/2 top-[5px] h-1 ${index <= currentStage ? "bg-primary" : "bg-muted"}`}
                  />
                )}
                {!last && (
                  <span
                    aria-hidden="true"
                    className={`absolute left-1/2 right-0 top-[5px] h-1 ${index < currentStage ? "bg-primary" : "bg-muted"}`}
                  />
                )}
                <span
                  aria-hidden="true"
                  className={`relative block h-3.5 w-3.5 rounded-full ${
                    isCurrent
                      ? "bg-forest ring-[3px] ring-lime-border"
                      : index < currentStage
                        ? "bg-primary"
                        : "border-2 border-border-strong bg-white"
                  }`}
                />
                <span
                  className={`mt-1.5 text-center text-[11px] ${isCurrent ? "font-extrabold text-forest" : "font-semibold text-subtle"}`}
                >
                  {stage.shortLabel}
                </span>
              </li>
            );
          })}
        </ol>

        <div className="mt-3 flex items-center justify-between gap-3 border-t border-border pt-2.5">
          <p className="min-w-0 truncate text-xs text-muted-foreground">
            Next:{" "}
            <strong className="font-semibold text-forest">{nextRelationshipAction(entry)}</strong>
          </p>
          <div className="flex shrink-0 items-center gap-1">
            <button
              type="button"
              onClick={event => {
                event.stopPropagation();
                onRemove(entry);
              }}
              className="grid h-8 w-8 place-items-center rounded-lg text-subtle opacity-0 transition hover:bg-warn-soft hover:text-warn focus:opacity-100 group-hover:opacity-100"
              aria-label={`Remove ${entry.school.schoolName} from My Schools`}
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
            <span className="inline-flex items-center gap-1 text-xs font-extrabold text-primary-text">
              Open <ArrowRight className="h-3.5 w-3.5" />
            </span>
          </div>
        </div>
      </div>
    </article>
  );
}
